<script setup>
import { useLink } from 'vue-router'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { resolveDeeplink } from '@/utils/pathUtils'
import Localizer from '@/api/Localizer';

const store = useStore();
const localizer = new Localizer(store);

const contextMenuEnabled = computed(() => store.getters['security/LeftNavContextMenuFeatureFlagValue']);

const props = defineProps({
    to: { type: [String, Object], required: true},
    activeClass: { type: String, default: "router-link-active" },
    exactActiveClass: { type: String, default: "router-link-exact-active" },
    title: { type: String, required: false }
})

const { navigate, href, isActive, isExactActive } = useLink(props);

const correctedHref = computed(() => {
    return window.location.origin + resolveDeeplink(href.value);
});

const showContextMenu = ref(false);
const contextMenuX = ref(0);
const contextMenuY = ref(0);

function onContextMenu(e) {
    if (!contextMenuEnabled.value) return;
    e.preventDefault();
    contextMenuX.value = e.clientX;
    contextMenuY.value = e.clientY;
    showContextMenu.value = true;
    setTimeout(() => {
        document.addEventListener('click', closeContextMenu, { once: true });
        document.addEventListener('contextmenu', closeContextMenu, { once: true });
    }, 0);
}

function closeContextMenu() {
    showContextMenu.value = false;
    document.removeEventListener('click', closeContextMenu);
    document.removeEventListener('contextmenu', closeContextMenu);
}

function openInNewTab() {
    window.open(correctedHref.value, '_blank');
    closeContextMenu();
}

function openInNewWindow() {
    const width = window.screen.width * 0.8;
    const height = window.screen.height * 0.8;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    window.open(correctedHref.value, '_blank', `noopener,noreferrer,width=${width},height=${height},left=${left},top=${top}`);
    closeContextMenu();
}

function copyLink() {
    navigator.clipboard.writeText(correctedHref.value);
    closeContextMenu();
}

</script>

<template>

    <button @click="navigate" @contextmenu="onContextMenu" class="app-router-link" :class="[isActive ? activeClass : '', isExactActive ? exactActiveClass : '']" type="button" :title="title">
        <slot></slot>
        <Teleport to="body">
            <div v-if="showContextMenu" class="custom-context-menu" :style="{ left: contextMenuX + 'px', top: contextMenuY + 'px' }">
                <div class="context-menu-item" @click="openInNewTab">{{ localizer.Localize('Open_link_in_new_tab') }}</div>
                <div class="context-menu-item" @click="openInNewWindow">{{ localizer.Localize('Open_link_in_new_window') }}</div>
                <div class="context-menu-separator"></div>
                <div class="context-menu-item" @click="copyLink">{{ localizer.Localize('Copy_link_address') }}</div>
            </div>
        </Teleport>
    </button>

</template>

<style scoped>

    button {
        appearance: none;
        -webkit-appearance: none;
        border: none;
        background: none;
        padding: 0;
        margin: 0;
        font: inherit;
        color: inherit;
        text-align: inherit;
        cursor: pointer;
        display: flex;
        align-items: center;
    }

    button:focus-visible {
        outline: var(--anaplan-background) auto 1px;
        outline-offset: -1px;
    }

    .custom-context-menu {
        position: fixed;
        z-index: 10000;
        background: var(--sidebar-background);
        border: 1px solid var(--thin-border);
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        padding: 4px 0;
        min-width: 180px;
    }

    .context-menu-item {
        padding: 6px 16px;
        cursor: pointer;
        font-size: 13px;
        white-space: nowrap;
    }

    .context-menu-item:hover {
        background-color: var(--sidebar-btn-hover);
    }

    .context-menu-separator {
        height: 1px;
        background-color: var(--thin-border);
        margin: 4px 0;
    }

</style>